import React from 'react';
import { 
  ShieldCheck, FileText, Smartphone, MapPin, 
  Search, CheckCircle, XCircle, Info, Mail, 
  ExternalLink, AlertTriangle, ClipboardList, 
  UserPlus
} from 'lucide-react';
import { Link } from 'react-router-dom';

export default function WarrantyClaim() {
    const CONTAINER_MAX_WIDTH = "max-w-[1350px]";

    return (
        <div className="bg-[#F5F4F5] min-h-screen py-2 px-4 font-sans">
            <div className={`${CONTAINER_MAX_WIDTH} mx-auto`}>
                
                {/* Header Card */}
                <div className="bg-white min-h-[250px] shadow-sm border border-gray-200 rounded-xl p-6 mb-8 flex items-center justify-center space-x-3">
                    <div className="bg-[#2563eb] p-2 rounded-lg text-white shadow-md">
                        <ShieldCheck size={28} />
                    </div>
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-gray-800 tracking-tight">Warranty Claim</h1>
                        <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">Hassle-Free Support Process</p>
                    </div>
                </div>

                {/* Main Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
                    
                    {/* Left Side: Claim Steps */}
                    <div className="lg:col-span-2 bg-white shadow-sm rounded-xl border border-gray-100 min-h-[1000px] flex flex-col overflow-hidden">
                        
                        <div className="p-6 md:p-10 space-y-10 flex-grow text-gray-700">
                            
                            {/* Intro Section */}
                            <section>
                                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2 border-b pb-2">
                                    <ClipboardList size={22} className="text-[#2563eb]" /> Claim Instructions
                                </h2>
                                <p className="text-sm leading-relaxed text-gray-600">
                                    If you want to claim warranty service, follow the steps below. We aim to resolve your technical issues as swiftly as possible to get your device back in your hands.
                                </p>
                            </section>

                            {/* Step 1 & 2 */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <section className="space-y-3 bg-blue-50/30 p-5 rounded-lg border border-blue-50">
                                    <h3 className="font-bold flex items-center gap-2 text-gray-800">
                                        <FileText size={18} className="text-blue-600" /> Step 1: Prepare Documents
                                    </h3>
                                    <ul className="text-[14px] space-y-2 text-gray-600">
                                        <li className="flex items-center gap-2"><span>•</span> Original Invoice / Job Sheet</li>
                                        <li className="flex items-center gap-2"><span>•</span> Device with repaired part</li>
                                    </ul>
                                </section>

                                <section className="space-y-3 bg-gray-50 p-5 rounded-lg border border-gray-100">
                                    <h3 className="font-bold flex items-center gap-2 text-gray-800">
                                        <CheckCircle size={18} className="text-green-500" /> Step 2: Check Eligibility
                                    </h3>
                                    <p className="text-[13px] font-semibold text-gray-500 uppercase tracking-tighter">Your device must:</p>
                                    <ul className="text-[14px] space-y-1 text-gray-600">
                                        <li className="flex items-center gap-2"><CheckCircle size={12} className="text-green-500"/> Within warranty period</li>
                                        <li className="flex items-center gap-2"><XCircle size={12} className="text-red-400"/> NO physical damage</li>
                                        <li className="flex items-center gap-2"><XCircle size={12} className="text-red-400"/> NO water damage</li>
                                        <li className="flex items-center gap-2"><XCircle size={12} className="text-red-400"/> Not repaired by 3rd party</li>
                                    </ul>
                                </section>
                            </div>

                            {/* Step 3 & 4 */}
                            <section className="space-y-6">
                                <div className="flex gap-4 p-4 border-l-4 border-[#2563eb] bg-blue-50/20">
                                    <MapPin className="text-[#2563eb] shrink-0" size={20} />
                                    <div>
                                        <h3 className="font-bold text-gray-900">Step 3: Visit Service Center</h3>
                                        <p className="text-sm mt-1 text-gray-600 italic">
                                            Bring your device to the nearest <span className="font-bold text-gray-800">Crack Point Powered by PhoneLab</span> center.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex gap-4 p-4 border-l-4 border-orange-400 bg-orange-50/20">
                                    <Search className="text-orange-500 shrink-0" size={20} />
                                    <div>
                                        <h3 className="font-bold text-gray-900">Step 4: Inspection</h3>
                                        <p className="text-sm mt-1 text-gray-600">Our technician will verify the issue and check warranty conditions on-site.</p>
                                    </div>
                                </div>
                            </section>

                            {/* Step 5: Resolution - No background color */}
                            <section className="p-6 rounded-xl">
                                <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                    <AlertTriangle size={20} className="text-yellow-500" /> Step 5: Resolution
                                </h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="border-t border-gray-200 pt-3">
                                        <h4 className="text-green-600 font-bold text-sm uppercase">If Approved:</h4>
                                        <p className="text-sm text-gray-600 mt-1">Free repair OR replacement of the faulty component.</p>
                                    </div>
                                    <div className="border-t border-gray-200 pt-3">
                                        <h4 className="text-red-500 font-bold text-sm uppercase">If Rejected:</h4>
                                        <p className="text-sm text-gray-600 mt-1">A paid repair solution will be suggested based on diagnostics.</p>
                                    </div>
                                </div>
                            </section>

                        </div>

                        {/* Footer Contact */}
                        <div className="bg-gray-100 p-8 border-t border-gray-200 mt-auto">
                            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                                <p className="font-bold text-gray-800 flex items-center gap-2 text-sm md:text-base">
                                    <Info className="text-[#2563eb]" size={18} /> Need help with your claim?
                                </p>
                                <Link 
                                    to="mailto:support@phonelab.one" 
                                    className="flex items-center gap-2 text-[#2563eb] font-bold hover:underline bg-white px-4 py-2 rounded-full shadow-sm border border-blue-100"
                                >
                                    <Mail size={18} /> support@phonelab.one
                                </Link>
                            </div>
                        </div>
                    </div>

                    {/* Right Side: Quick Action / Status */}
                   <div className="lg:col-span-1">
                        <div className="sticky top-5 bg-white shadow-xl rounded-xl border border-gray-100 h-[600px] flex items-center justify-center">
                            <div className="text-center p-6">
                                <div className="bg-gray-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-dashed border-gray-200">
                                    <UserPlus className="text-gray-300" />
                                </div>
                                <h3 className="text-lg font-bold text-gray-800">Fast Booking</h3>
                                <p className="text-sm text-gray-400 mt-2 italic px-4">Our booking component stays sticky for easy access.</p>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}